/**
 * MyBuiltAgents — list of agents I own + agents shared with me (AA-210).
 *
 * Reads /api/me/agents and renders three sections:
 *   - Owned:        tabbed card with Overview / Share / Indexing / Edit
 *   - Shared with me: read-only, opens chat
 *   - Available to me: published agents reachable via SharePoint membership
 *
 * Companion page to BuildAnAgent. Wired into Sidebar as a top-level
 * entry so end users can find and manage their personal agents
 * without going through the admin dashboard.
 *
 * AA-210 redesign: the visibility dropdown (private/shared/published)
 * and the audience picker (roles/jobs/locations) are gone. Sharing is
 * now expressed as: pick individuals from the agent's connected
 * SharePoint site, or flip "Share with all members of {SiteName}" to
 * publish to the whole site. Visibility values still exist on the
 * Neo4j record; they're updated as a side effect of the new actions.
 */

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Plus,
  PowerOff,
  Edit3,
  Share2,
  Users,
  Globe,
  MessageCircle,
  AlertCircle,
  CheckCircle2,
  X,
  Folder,
  RefreshCw,
  Search as SearchIcon,
  Trash2,
  Mail,
  Lock,
} from 'lucide-react';
import {
  listMyAgents,
  deleteMyAgent,
  updateMyAgent,
  addSharingGrants,
  removeSharingGrant,
  listSharing,
  listAgentSharePointMembers,
  setShareWithAllMembers,
  getMyAgentIndexingStatus,
  reindexMyAgent,
  MyAgent,
  IndexingStatus,
  SharingState,
  SharePointSiteMember,
  AvailableToMeAgent,
  AccessVia,
} from '../services/myAgents';

const MyBuiltAgents: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [owned, setOwned] = useState<MyAgent[]>([]);
  const [sharedWithMe, setSharedWithMe] = useState<MyAgent[]>([]);
  // Discovery surface: published self-service agents the caller can
  // reach via the SharePoint-membership gate but doesn't own and
  // isn't explicitly shared with. Read-only here (cards open chat).
  const [availableToMe, setAvailableToMe] = useState<AvailableToMeAgent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const justCreatedId = searchParams.get('created');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listMyAgents();
      setOwned(data.owned);
      setSharedWithMe(data.sharedWithMe);
      setAvailableToMe(data.availableToMe);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load agents');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Disable (not delete): the API is `DELETE /api/me/agents/:id` but
  // the underlying call is `graphConfigService.deleteAgent(soft=true)`
  // which just sets `disabled: true` on the AIAgent node. The agent
  // stops showing up in the chat picker and is hidden from this page,
  // but it stays in Neo4j with all its config and sharing intact —
  // an admin can re-enable it via the dashboard. Calling it "Delete"
  // implied irrecoverable removal, which it isn't.
  const handleDisable = async (agent: MyAgent) => {
    const ok = window.confirm(
      `Disable "${agent.name}"? It'll be hidden from your list and the chat picker, ` +
        `and any sharing grants you've made stop working. You (or an admin) can ` +
        `re-enable it from the admin dashboard later — nothing is permanently deleted.`
    );
    if (!ok) return;
    try {
      await deleteMyAgent(agent.agentId);
      load();
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Disable failed');
    }
  };

  const handleReindex = async (agent: MyAgent) => {
    const ok = window.confirm(
      `Reindex "${agent.name}"? This re-runs the SharePoint folder ingestion ` +
        `against ${agent.sourceFolder || '(library root)'}. The agent will fall ` +
        `back to its previous index until the new run completes (usually a minute or two).`
    );
    if (!ok) return;
    try {
      await reindexMyAgent(agent.agentId);
      load();
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Reindex failed');
    }
  };

  return (
    <div
      // Outer scroll container — AppContent main area is overflow-hidden.
      style={{
        height: '100%',
        overflowY: 'auto',
        background: 'var(--bg-primary, #f8f9fa)',
      }}
    >
    <div
      style={{
        maxWidth: 1080,
        margin: '0 auto',
        padding: '32px 24px 64px',
        color: 'var(--text-primary, #1f2124)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 28, fontWeight: 700 }}>My Agents</h1>
          <p style={{ margin: '6px 0 0', color: 'var(--text-secondary, #53575d)' }}>
            Personal agents you created and agents others have shared with you.
          </p>
        </div>
        <button
          type="button"
          onClick={() => navigate('/build-agent')}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '10px 14px',
            borderRadius: 8,
            border: 'none',
            background: 'var(--brand-600, #047857)',
            color: '#fff',
            fontWeight: 500,
            cursor: 'pointer',
          }}
        >
          <Plus size={16} /> Build an Agent
        </button>
      </div>

      {justCreatedId && (
        <div
          style={{
            padding: 12,
            background: 'var(--brand-50, #ecfdf5)',
            border: '1px solid var(--brand-200, #a7f3d0)',
            borderRadius: 8,
            color: 'var(--brand-800, #065f46)',
            fontSize: 14,
            marginBottom: 16,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            justifyContent: 'space-between',
          }}
        >
          <span>
            <CheckCircle2 size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />
            {/*
              Show the user-facing agent name when we can find the
              freshly-created agent in `owned`. The query param holds
              the auto-generated slug (with the 4-char suffix) which
              is internal — surfacing it here read as system noise.
              Fall back to a generic phrasing if the page loads
              before the create has propagated through listMyAgents.
            */}
            {(() => {
              const justCreated = owned.find((a) => a.agentId === justCreatedId);
              return justCreated
                ? `Created "${justCreated.name}".`
                : 'Agent created.';
            })()}{' '}
            It may take up to a minute to appear in the general agent list while the platform reloads.
          </span>
          <button
            type="button"
            onClick={() => {
              const next = new URLSearchParams(searchParams);
              next.delete('created');
              setSearchParams(next);
            }}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              color: 'inherit',
            }}
          >
            <X size={14} />
          </button>
        </div>
      )}

      {loading ? (
        <div>Loading…</div>
      ) : error ? (
        <div
          style={{
            padding: 12,
            color: '#991b1b',
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: 6,
          }}
        >
          <AlertCircle size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />
          {error}
        </div>
      ) : (
        <>
          <Section title={`Owned (${owned.length})`}>
            {owned.length === 0 ? (
              <EmptyOwned onBuild={() => navigate('/build-agent')} />
            ) : (
              <div style={{ display: 'grid', gap: 12 }}>
                {owned.map((agent) => (
                  <OwnedAgentCard
                    key={agent.agentId}
                    agent={agent}
                    onChat={() => navigate(`/?agent=${encodeURIComponent(agent.agentId)}`)}
                    onDisable={() => handleDisable(agent)}
                    onReindex={() => handleReindex(agent)}
                    onReload={load}
                  />
                ))}
              </div>
            )}
          </Section>

          {sharedWithMe.length > 0 && (
            <Section title={`Shared with me (${sharedWithMe.length})`}>
              <div style={{ display: 'grid', gap: 12 }}>
                {sharedWithMe.map((agent) => (
                  <SharedAgentCard
                    key={agent.agentId}
                    agent={agent}
                    onChat={() => navigate(`/?agent=${encodeURIComponent(agent.agentId)}`)}
                  />
                ))}
              </div>
            </Section>
          )}

          {availableToMe.length > 0 && (
            <Section
              title={`Available to me (${availableToMe.length})`}
              subtitle="Agents you can use because the owner shared them with everyone in their SharePoint site and you have access to that site."
            >
              <div style={{ display: 'grid', gap: 12 }}>
                {availableToMe.map((agent) => (
                  <AvailableAgentCard
                    key={agent.agentId}
                    agent={agent}
                    onChat={() => navigate(`/?agent=${encodeURIComponent(agent.agentId)}`)}
                  />
                ))}
              </div>
            </Section>
          )}
        </>
      )}
    </div>
    </div>
  );
};

const Section: React.FC<{
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}> = ({ title, subtitle, children }) => (
  <section style={{ marginTop: 32 }}>
    <h2 style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-secondary, #53575d)', marginBottom: subtitle ? 4 : 10 }}>
      {title}
    </h2>
    {subtitle && (
      <p
        style={{
          fontSize: 13,
          color: 'var(--text-tertiary, #6f747b)',
          marginTop: 0,
          marginBottom: 12,
          maxWidth: 640,
          lineHeight: 1.45,
        }}
      >
        {subtitle}
      </p>
    )}
    {children}
  </section>
);

const EmptyOwned: React.FC<{ onBuild: () => void }> = ({ onBuild }) => (
  <div
    style={{
      padding: 32,
      textAlign: 'center',
      border: '1px dashed var(--border, #cfd2d6)',
      borderRadius: 12,
      background: 'var(--bg-secondary, #f8f9fa)',
    }}
  >
    <div style={{ fontSize: 18, fontWeight: 600 }}>No personal agents yet</div>
    <div style={{ color: 'var(--text-secondary, #6f747b)', marginTop: 6, marginBottom: 14 }}>
      Build one from a template — it takes about a minute.
    </div>
    <button
      type="button"
      onClick={onBuild}
      style={{
        padding: '10px 16px',
        borderRadius: 8,
        border: 'none',
        background: 'var(--brand-600, #047857)',
        color: '#fff',
        fontWeight: 500,
        cursor: 'pointer',
      }}
    >
      <Plus size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />
      Build an Agent
    </button>
  </div>
);

// Knowledge-base ingestion badge for self-service agents (Phase D, AA-207).
// Polls /api/me/agents/:id/indexing-status every 10s while in pending/
// indexing and notifies the parent when a terminal state is reached so the
// list can refresh and the badge can settle.
const IndexingBadge: React.FC<{
  agent: MyAgent;
  onSettled: () => void;
}> = ({ agent, onSettled }) => {
  const [liveStatus, setLiveStatus] = useState<IndexingStatus | undefined>(
    agent.indexingStatus
  );
  const [liveError, setLiveError] = useState<string | null>(
    agent.indexingError ?? null
  );

  useEffect(() => {
    setLiveStatus(agent.indexingStatus);
    setLiveError(agent.indexingError ?? null);
  }, [agent.indexingStatus, agent.indexingError]);

  useEffect(() => {
    if (liveStatus !== 'pending' && liveStatus !== 'indexing') return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;

    const tick = async () => {
      try {
        const r = await getMyAgentIndexingStatus(agent.agentId);
        if (cancelled) return;
        if (r.status !== liveStatus) {
          setLiveStatus(r.status);
          setLiveError(r.error);
          if (r.status === 'ready' || r.status === 'failed') {
            onSettled();
            return;
          }
        }
      } catch {
        // Transient errors — keep polling at the same cadence.
      }
      if (!cancelled) timer = setTimeout(tick, 10_000);
    };

    timer = setTimeout(tick, 10_000);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [agent.agentId, liveStatus, onSettled]);

  if (!liveStatus || liveStatus === 'unconfigured') return null;

  const styles: Record<IndexingStatus, React.CSSProperties> = {
    unconfigured: {},
    pending: {
      background: '#fef3c7',
      color: '#92400e',
      border: '1px solid #fde68a',
    },
    indexing: {
      background: '#dbeafe',
      color: '#1e40af',
      border: '1px solid #bfdbfe',
    },
    ready: {
      background: '#dcfce7',
      color: '#166534',
      border: '1px solid #bbf7d0',
    },
    failed: {
      background: '#fee2e2',
      color: '#991b1b',
      border: '1px solid #fecaca',
    },
  };

  const labels: Record<IndexingStatus, string> = {
    unconfigured: '',
    pending: 'Knowledge base queued…',
    indexing: 'Knowledge base loading…',
    ready: 'Knowledge base ready',
    failed: 'Knowledge base failed',
  };

  return (
    <span
      title={liveError || labels[liveStatus]}
      style={{
        fontSize: 11,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '2px 8px',
        borderRadius: 999,
        maxWidth: liveStatus === 'failed' ? 480 : undefined,
        ...styles[liveStatus],
      }}
    >
      <span>{labels[liveStatus]}</span>
      {liveStatus === 'failed' && liveError && (
        <span
          style={{
            fontWeight: 400,
            opacity: 0.85,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: 360,
          }}
        >
          — {liveError}
        </span>
      )}
    </span>
  );
};

/**
 * Render a compact "{n} folder(s)" / "library root" summary for the
 * card's knowledge-source row. Multi-folder agents now persist a
 * JSON array on `sourceFolders`, but older records may only have
 * `sourceFolder`. Fall through cleanly so a card never says
 * "undefined folders".
 */
function _summarizeFolders(agent: MyAgent): string {
  const folders =
    Array.isArray(agent.sourceFolders) && agent.sourceFolders.length > 0
      ? (agent.sourceFolders as string[])
      : typeof agent.sourceFolder === 'string' && agent.sourceFolder.trim()
        ? [agent.sourceFolder.trim()]
        : [];
  if (folders.length === 0) return '(library root)';
  if (folders.length === 1) return folders[0];
  return `${folders.length} folders`;
}

/**
 * Pull a hostname/site label out of the SharePoint web URL we stored
 * on the agent. Used as the headline label for the knowledge-source
 * row — the full URL is preserved as the title so power users still
 * have it on hover.
 */
function _siteLabel(agent: MyAgent): string {
  const url = (agent.sourceSiteWebUrl as string) || '';
  if (url) {
    const m = url.match(/\/(?:sites|teams)\/([^/?#]+)/i);
    if (m) return decodeURIComponent(m[1]);
    try {
      return new URL(url).hostname;
    } catch {
      // fall through
    }
  }
  return (agent.sourceSite as string) || 'SharePoint';
}

type OwnedTabId = 'overview' | 'share' | 'indexing' | 'edit';

const OwnedAgentCard: React.FC<{
  agent: MyAgent;
  onChat: () => void;
  onDisable: () => void;
  onReindex: () => void;
  onReload: () => void;
}> = ({ agent, onChat, onDisable, onReindex, onReload }) => {
  const [activeTab, setActiveTab] = useState<OwnedTabId>('overview');

  const hasKnowledgeSource = Boolean(agent.sourceSiteWebUrl || agent.sourceFolder);
  // Server-attached sharing summary (see /api/me/agents). shareCount
  // is the total `allowed` grants; shareEmails is the first 3 so the
  // Overview can render "alice@…, bob@… +2 more" without round-trips.
  const shareCount = typeof agent.shareCount === 'number' ? agent.shareCount : 0;
  const shareEmails = Array.isArray(agent.shareEmails) ? agent.shareEmails : [];
  const allMembersEnabled = agent.visibility === 'published';
  const reindexBusy =
    agent.indexingStatus === 'indexing' || agent.indexingStatus === 'pending';

  return (
    <div
      style={{
        border: '1px solid var(--border, #e3e5e8)',
        borderRadius: 12,
        background: 'var(--bg-card, #fff)',
        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
        overflow: 'hidden',
      }}
    >
      {/* Header row — name, sharing-state pill, indexing badge, Chat. */}
      <div
        style={{
          padding: '14px 16px 10px',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', minWidth: 0 }}>
          <strong style={{ fontSize: 16, color: 'var(--text-primary, #1f2124)' }}>
            {agent.name}
          </strong>
          <SharingStatePill agent={agent} />
          <IndexingBadge agent={agent} onSettled={onReload} />
        </div>
        <div style={{ flexShrink: 0 }}>
          <button
            type="button"
            onClick={onChat}
            title="Open chat with this agent"
            style={primaryBtnStyle}
          >
            <MessageCircle size={14} /> Chat
          </button>
        </div>
      </div>

      {/* Description — never shows "undefined" or "null"; falls through
          to a placeholder when the owner left it blank. */}
      <div
        style={{
          padding: '0 16px',
          color: 'var(--text-secondary, #53575d)',
          fontSize: 13,
          lineHeight: 1.45,
        }}
      >
        {agent.description ? (
          agent.description
        ) : (
          <em style={{ color: 'var(--text-tertiary, #9da2a8)' }}>
            No description yet — open the <strong>Edit</strong> tab to add one.
          </em>
        )}
      </div>

      {/* Tab strip — replaces the old action bar. Indexing tab is
          disabled when the agent has no SharePoint source; Edit and
          Share are always reachable. */}
      <TabStrip
        active={activeTab}
        onChange={setActiveTab}
        tabs={[
          { id: 'overview', label: 'Overview' },
          {
            id: 'share',
            label:
              shareCount > 0
                ? `Share (${shareCount})`
                : allMembersEnabled
                  ? 'Share (everyone)'
                  : 'Share',
          },
          {
            id: 'indexing',
            label: 'Indexing',
            disabled: !hasKnowledgeSource,
          },
          { id: 'edit', label: 'Edit' },
        ]}
      />

      {/* Tab content panel */}
      <div style={{ padding: '12px 16px 4px', background: 'var(--bg-subtle, #fafafa)' }}>
        {activeTab === 'overview' && (
          <OverviewTabContent
            agent={agent}
            shareCount={shareCount}
            shareEmails={shareEmails}
            allMembersEnabled={allMembersEnabled}
            hasKnowledgeSource={hasKnowledgeSource}
            onOpenShare={() => setActiveTab('share')}
            onOpenEdit={() => setActiveTab('edit')}
          />
        )}
        {activeTab === 'share' && (
          <ShareTabContent agent={agent} onReload={onReload} />
        )}
        {activeTab === 'indexing' && hasKnowledgeSource && (
          <IndexingTabContent
            agent={agent}
            reindexBusy={reindexBusy}
            onReindex={onReindex}
          />
        )}
        {activeTab === 'edit' && (
          <EditTabContent agent={agent} onSaved={onReload} />
        )}
      </div>

      {/* Persistent footer with the Disable action. Lives outside the
          tab panel because disabling is a card-level operation, not
          tied to any one tab. */}
      <div
        style={{
          padding: '10px 16px',
          borderTop: '1px solid var(--border-subtle, #f1f2f4)',
          display: 'flex',
          justifyContent: 'flex-end',
        }}
      >
        <button
          type="button"
          onClick={onDisable}
          title="Hide from your list and the chat picker. Can be recovered by an admin."
          style={dangerGhostBtnStyle}
        >
          <PowerOff size={13} /> Disable
        </button>
      </div>
    </div>
  );
};

/**
 * Tab strip used on the owned agent card. Plain unstyled <button>s
 * with a bottom-border active state to keep the card visually
 * lightweight; no heavy chrome since each card is its own panel.
 */
const TabStrip: React.FC<{
  active: OwnedTabId;
  onChange: (id: OwnedTabId) => void;
  tabs: Array<{ id: OwnedTabId; label: string; disabled?: boolean }>;
}> = ({ active, onChange, tabs }) => (
  <div
    style={{
      marginTop: 12,
      borderTop: '1px solid var(--border-subtle, #f1f2f4)',
      display: 'flex',
      gap: 4,
      padding: '0 12px',
      background: '#fff',
    }}
    role="tablist"
  >
    {tabs.map((t) => {
      const isActive = active === t.id;
      return (
        <button
          key={t.id}
          type="button"
          role="tab"
          aria-selected={isActive}
          aria-disabled={t.disabled || undefined}
          disabled={t.disabled}
          onClick={() => !t.disabled && onChange(t.id)}
          style={{
            padding: '10px 12px',
            background: 'transparent',
            border: 'none',
            borderBottom: isActive
              ? '2px solid var(--brand-600, #047857)'
              : '2px solid transparent',
            color: t.disabled
              ? '#9da2a8'
              : isActive
                ? 'var(--brand-700, #065f46)'
                : 'var(--text-secondary, #53575d)',
            fontSize: 13,
            fontWeight: isActive ? 600 : 500,
            cursor: t.disabled ? 'not-allowed' : 'pointer',
            opacity: t.disabled ? 0.6 : 1,
          }}
        >
          {t.label}
        </button>
      );
    })}
  </div>
);

/**
 * Sharing-state pill — replaces the old visibility pill (which leaked
 * the implementation detail "private/shared/published" to users). The
 * new pill expresses intent in user terms:
 *
 *   Private              — only you (visibility=private, no grants)
 *   Shared with N        — individuals (visibility=shared)
 *   Shared with everyone — visibility=published (all SharePoint members)
 *
 * Derived from the agent record only, so it stays accurate before the
 * Share tab fetches its detail state.
 */
const SharingStatePill: React.FC<{ agent: MyAgent }> = ({ agent }) => {
  const allMembers = agent.visibility === 'published';
  const shareCount = typeof agent.shareCount === 'number' ? agent.shareCount : 0;
  let palette: { bg: string; border: string; fg: string };
  let icon: React.ReactElement;
  let label: string;

  if (allMembers) {
    palette = { bg: '#ecfdf5', border: '#a7f3d0', fg: '#065f46' };
    icon = <Globe size={12} />;
    label = 'Shared with everyone';
  } else if (shareCount > 0 || agent.visibility === 'shared') {
    palette = { bg: '#eff6ff', border: '#bfdbfe', fg: '#1e3a8a' };
    icon = <Users size={12} />;
    label =
      shareCount > 0
        ? `Shared with ${shareCount}`
        : 'Shared (no one yet)';
  } else {
    palette = { bg: '#f1f2f4', border: '#e3e5e8', fg: '#3d4045' };
    icon = <Lock size={12} />;
    label = 'Private';
  }

  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: 500,
        color: palette.fg,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '2px 8px',
        background: palette.bg,
        border: `1px solid ${palette.border}`,
        borderRadius: 999,
      }}
    >
      {icon} {label}
    </span>
  );
};

// ---------------------------------------------------------------------------
// Overview tab — the resting view of an owned agent card. Shows the
// connected knowledge source, a quick sharing summary, and shortcuts
// to the Share / Edit tabs. Replaces the old card body sans-modals.
// ---------------------------------------------------------------------------

const OverviewTabContent: React.FC<{
  agent: MyAgent;
  shareCount: number;
  shareEmails: string[];
  allMembersEnabled: boolean;
  hasKnowledgeSource: boolean;
  onOpenShare: () => void;
  onOpenEdit: () => void;
}> = ({
  agent,
  shareCount,
  shareEmails,
  allMembersEnabled,
  hasKnowledgeSource,
  onOpenShare,
  onOpenEdit,
}) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, paddingBottom: 8 }}>
    {hasKnowledgeSource ? (
      <div
        title={
          typeof agent.sourceSiteWebUrl === 'string'
            ? (agent.sourceSiteWebUrl as string)
            : undefined
        }
        style={{
          fontSize: 12,
          color: 'var(--text-secondary, #53575d)',
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          minWidth: 0,
        }}
      >
        <Folder size={13} aria-hidden style={{ flexShrink: 0, color: '#6f747b' }} />
        <span style={{ fontWeight: 500 }}>{_siteLabel(agent)}</span>
        <span style={{ color: '#9da2a8' }}>·</span>
        <code
          style={{
            background: '#f1f2f4',
            padding: '1px 6px',
            borderRadius: 4,
            fontSize: 11,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: 360,
          }}
        >
          {_summarizeFolders(agent)}
        </code>
      </div>
    ) : (
      <div style={{ fontSize: 12, color: '#6f747b' }}>
        No knowledge source connected.
      </div>
    )}

    <div
      style={{
        fontSize: 12,
        color: 'var(--text-secondary, #53575d)',
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        flexWrap: 'wrap',
      }}
    >
      {allMembersEnabled ? (
        <>
          <Globe size={13} aria-hidden style={{ flexShrink: 0, color: '#6f747b' }} />
          <span style={{ fontWeight: 500 }}>
            Shared with everyone in {_siteLabel(agent)}
          </span>
          {shareCount > 0 && (
            <span style={{ color: '#6f747b' }}>
              · plus {shareCount} explicit grant{shareCount === 1 ? '' : 's'}
            </span>
          )}
        </>
      ) : shareCount > 0 ? (
        <>
          <Users size={13} aria-hidden style={{ flexShrink: 0, color: '#6f747b' }} />
          <span style={{ fontWeight: 500 }}>
            Shared with {shareCount} teammate{shareCount === 1 ? '' : 's'}
          </span>
          {shareEmails.length > 0 && (
            <span style={{ color: '#6f747b' }}>
              · {shareEmails.join(', ')}
              {shareCount > shareEmails.length
                ? ` +${shareCount - shareEmails.length} more`
                : ''}
            </span>
          )}
        </>
      ) : (
        <>
          <Lock size={13} aria-hidden style={{ flexShrink: 0, color: '#6f747b' }} />
          <span style={{ color: '#6f747b' }}>
            Private — only you can use this agent.
          </span>
          <button
            type="button"
            onClick={onOpenShare}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--brand-700, #065f46)',
              fontWeight: 500,
              cursor: 'pointer',
              padding: 0,
              marginLeft: 4,
              fontSize: 12,
            }}
          >
            Invite teammates →
          </button>
        </>
      )}
    </div>

    <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
      <button
        type="button"
        onClick={onOpenShare}
        title="Manage who can use this agent"
        style={ghostBtnStyle}
      >
        <Share2 size={13} /> Manage sharing
      </button>
      <button
        type="button"
        onClick={onOpenEdit}
        title="Edit name, description, instructions"
        style={ghostBtnStyle}
      >
        <Edit3 size={13} /> Edit details
      </button>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Share tab — AA-210 redesigned sharing UI.
//
// Loads:
//   • current sharing state (grants + allMembersEnabled)
//   • SharePoint members of the agent's source site, filtered by a
//     debounced search input
//
// Actions:
//   • toggle "Share with all members of {SiteName}" (with confirm-by-count)
//   • check-mark members + Add selected (batched grant)
//   • remove individual grants
//   • email-fallback for non-SharePoint agents
// ---------------------------------------------------------------------------

const ShareTabContent: React.FC<{
  agent: MyAgent;
  onReload: () => void;
}> = ({ agent, onReload }) => {
  const [sharingState, setSharingState] = useState<SharingState | null>(null);
  const [members, setMembers] = useState<SharePointSiteMember[]>([]);
  const [hasSharePointSource, setHasSharePointSource] = useState<boolean | null>(null);
  const [siteName, setSiteName] = useState<string | null>(null);
  const [siteTotalCount, setSiteTotalCount] = useState(0);
  // AA-210: when /sites/{id}/permissions returns 401/403 the backend
  // surfaces a flag here so the empty-state copy can explain the
  // permission gap rather than read as "no members at all".
  const [permissionsAccessDenied, setPermissionsAccessDenied] = useState(false);

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [emailFallback, setEmailFallback] = useState('');

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const reloadSharing = useCallback(async () => {
    try {
      const state = await listSharing(agent.agentId);
      setSharingState(state);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to load sharing state');
    }
  }, [agent.agentId]);

  // Initial load: sharing state + first page of members.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      listSharing(agent.agentId).catch((e) => {
        if (!cancelled) setErr(e instanceof Error ? e.message : 'Failed to load sharing state');
        return null;
      }),
      listAgentSharePointMembers(agent.agentId, { top: 100 }).catch(() => null),
    ]).then(([state, members]) => {
      if (cancelled) return;
      if (state) setSharingState(state);
      if (members) {
        setMembers(members.members || []);
        setHasSharePointSource(members.hasSharePointSource);
        setSiteName(members.site?.displayName || null);
        setSiteTotalCount(members.totalCount || 0);
        setPermissionsAccessDenied(!!members.permissionsAccessDenied);
      } else {
        setHasSharePointSource(false);
      }
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [agent.agentId]);

  // Debounce search input.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 250);
    return () => clearTimeout(t);
  }, [search]);

  // Refetch members on debounced search change.
  useEffect(() => {
    if (hasSharePointSource !== true) return;
    let cancelled = false;
    listAgentSharePointMembers(agent.agentId, { search: debouncedSearch, top: 100 })
      .then((resp) => {
        if (cancelled) return;
        setMembers(resp.members || []);
        setSiteTotalCount(resp.totalCount || 0);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [debouncedSearch, hasSharePointSource, agent.agentId]);

  const grantedEmails = useMemo(() => {
    const set = new Set<string>();
    (sharingState?.sharing || []).forEach((g) => set.add(g.userEmail.toLowerCase()));
    return set;
  }, [sharingState]);

  const allMembersEnabled = sharingState?.allMembersEnabled ?? agent.visibility === 'published';

  const toggleSelected = (email: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(email)) next.delete(email);
      else next.add(email);
      return next;
    });
  };

  const handleAddSelected = async () => {
    if (selected.size === 0) return;
    setBusy(true);
    setErr(null);
    try {
      const result = await addSharingGrants(agent.agentId, Array.from(selected));
      setSelected(new Set());
      await reloadSharing();
      onReload();
      if (result.failed && result.failed.length > 0) {
        setErr(
          `Granted ${result.granted.length}, failed ${result.failed.length}: ` +
            result.failed.map((f) => `${f.userEmail} (${f.error})`).join('; ')
        );
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Add failed');
    } finally {
      setBusy(false);
    }
  };

  const handleAddEmail = async () => {
    const email = emailFallback.trim();
    if (!email) return;
    setBusy(true);
    setErr(null);
    try {
      await addSharingGrants(agent.agentId, [email]);
      setEmailFallback('');
      await reloadSharing();
      onReload();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Add failed');
    } finally {
      setBusy(false);
    }
  };

  const handleRemoveGrant = async (email: string) => {
    setBusy(true);
    setErr(null);
    try {
      await removeSharingGrant(agent.agentId, email);
      await reloadSharing();
      onReload();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Remove failed');
    } finally {
      setBusy(false);
    }
  };

  const handleToggleAllMembers = async () => {
    if (!allMembersEnabled) {
      // Confirm-by-count: surface the actual audience size before
      // flipping. siteTotalCount is the unfiltered total returned
      // alongside the (filtered) members list.
      const audienceMsg =
        siteTotalCount > 0
          ? `This will give ${siteTotalCount} ${
              siteTotalCount === 1 ? 'person' : 'people'
            } with access to "${siteName || 'the connected SharePoint site'}" the ability to use this agent.`
          : `This will give everyone with access to "${
              siteName || 'the connected SharePoint site'
            }" the ability to use this agent.`;
      const ok = window.confirm(
        `Share "${agent.name}" with all SharePoint members?\n\n${audienceMsg}\n\n` +
          `You can flip this back off any time.`
      );
      if (!ok) return;
    }

    setBusy(true);
    setErr(null);
    try {
      await setShareWithAllMembers(agent.agentId, !allMembersEnabled);
      await reloadSharing();
      onReload();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Toggle failed');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <div style={{ fontSize: 13, color: '#6f747b', padding: 8 }}>Loading sharing…</div>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, paddingBottom: 8 }}>
      {/* All-members toggle */}
      {hasSharePointSource && siteName && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            padding: '10px 12px',
            border: '1px solid var(--border, #e3e5e8)',
            borderRadius: 8,
            background: allMembersEnabled ? '#ecfdf5' : '#fff',
          }}
        >
          <div style={{ minWidth: 0, fontSize: 13 }}>
            <div style={{ fontWeight: 600, color: '#1f2124' }}>
              Share with all members of {siteName}
            </div>
            <div style={{ color: '#6f747b', fontSize: 12, marginTop: 2 }}>
              {allMembersEnabled
                ? `On — anyone with access to "${siteName}" can use this agent.`
                : siteTotalCount > 0
                  ? `Off — flip to give ${siteTotalCount} site member${
                      siteTotalCount === 1 ? '' : 's'
                    } the ability to use this agent.`
                  : `Off — flip to share with everyone who has access to "${siteName}".`}
            </div>
          </div>
          <button
            type="button"
            onClick={handleToggleAllMembers}
            disabled={busy}
            style={{
              ...iconBtnStyle,
              background: allMembersEnabled ? '#fff' : 'var(--brand-600, #047857)',
              color: allMembersEnabled ? '#3d4045' : '#fff',
              borderColor: allMembersEnabled ? 'var(--border, #cfd2d6)' : 'transparent',
              flexShrink: 0,
            }}
          >
            {allMembersEnabled ? 'Stop sharing with all' : 'Share with all members'}
          </button>
        </div>
      )}

      {/*
        Member picker (when SharePoint source) PLUS email fallback.
        The picker enumerates members via Graph from the connected
        SharePoint site, but that path quietly fails or returns empty
        in several real-world cases (classic-team sites, sites where
        the app principal lacks Sites.FullControl, very large M365
        groups, etc.). To make sure the owner is never blocked from
        sharing, we always render the email entry below — even when
        the picker did return rows. The email path skips the
        SharePoint membership check and grants directly via the
        AgentAccessLists table.
      */}
      {hasSharePointSource ? (
        <div>
          <div style={{ fontSize: 12, fontWeight: 500, color: '#3d4045', marginBottom: 6 }}>
            Invite specific teammates
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              border: '1px solid var(--border, #cfd2d6)',
              borderRadius: 8,
              padding: '6px 10px',
              background: '#fff',
            }}
          >
            <SearchIcon size={14} aria-hidden style={{ color: '#9da2a8', flexShrink: 0 }} />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={
                siteName
                  ? `Search ${siteName} members by name or email`
                  : 'Search site members'
              }
              style={{
                flex: 1,
                border: 'none',
                outline: 'none',
                background: 'transparent',
                fontSize: 13,
              }}
            />
          </div>
          <div
            style={{
              marginTop: 8,
              maxHeight: 220,
              overflowY: 'auto',
              border: '1px solid var(--border, #e3e5e8)',
              borderRadius: 8,
              background: '#fff',
            }}
          >
            {members.length === 0 ? (
              <div style={{ padding: 14, fontSize: 13, color: '#6f747b' }}>
                {debouncedSearch
                  ? `No members match "${debouncedSearch}".`
                  : 'No members found in this SharePoint site.'}
              </div>
            ) : (
              members.map((m) => {
                const already = grantedEmails.has(m.email.toLowerCase());
                const checked = selected.has(m.email);
                return (
                  <label
                    key={m.email}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '8px 12px',
                      borderBottom: '1px solid var(--border-subtle, #f1f2f4)',
                      cursor: already ? 'default' : 'pointer',
                      opacity: already ? 0.5 : 1,
                    }}
                  >
                    <input
                      type="checkbox"
                      disabled={already}
                      checked={already || checked}
                      onChange={() => !already && toggleSelected(m.email)}
                    />
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 500, color: '#1f2124' }}>
                        {m.displayName}
                        {already && (
                          <span style={{ color: '#9da2a8', fontWeight: 400, marginLeft: 6 }}>
                            (already shared)
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: 11, color: '#6f747b' }}>{m.email}</div>
                    </div>
                  </label>
                );
              })
            )}
          </div>
          <div style={{ marginTop: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontSize: 12, color: '#6f747b' }}>
              {selected.size > 0
                ? `${selected.size} selected`
                : siteTotalCount > 0
                  ? `${members.length} of ${siteTotalCount} member${siteTotalCount === 1 ? '' : 's'} shown`
                  : ''}
            </div>
            <button
              type="button"
              onClick={handleAddSelected}
              disabled={busy || selected.size === 0}
              style={{
                ...iconBtnStyle,
                background: 'var(--brand-600, #047857)',
                color: '#fff',
                borderColor: 'transparent',
                opacity: selected.size === 0 ? 0.5 : 1,
                cursor: selected.size === 0 ? 'not-allowed' : 'pointer',
              }}
            >
              Add selected
            </button>
          </div>
        </div>
      ) : null}

      {/*
        Email entry. Always rendered: it serves as both the primary
        affordance when there's no SharePoint source and a fallback
        when the picker came back empty (most often a SharePoint
        permissions gap on the app principal). The copy adapts based
        on which case we're in so it doesn't read awkwardly when the
        picker is already showing members above.
      */}
      <div>
        <div style={{ fontSize: 12, fontWeight: 500, color: '#3d4045', marginBottom: 6 }}>
          {hasSharePointSource ? 'Or invite by email' : 'Invite teammates by email'}
        </div>
        <div style={{ fontSize: 12, color: '#6f747b', marginBottom: 8 }}>
          {hasSharePointSource
            ? members.length === 0
              ? permissionsAccessDenied
                ? 'The SharePoint app principal doesn’t have permission to enumerate this site’s members (it has Sites.Read.All but not Sites.FullControl.All). You can still grant access by email — recipients don’t need to be in the SharePoint site to use the agent.'
                : 'We couldn’t list members for this SharePoint site (likely a classic site with no connected Microsoft 365 group). You can still grant access by email — recipients don’t need to be in the SharePoint site to use the agent.'
              : 'Don’t see who you’re looking for? Anyone with a UniFirst email can be granted access, whether or not they belong to the SharePoint site.'
            : 'This agent has no SharePoint source attached, so we can’t list members for you. Add teammates by email instead.'}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            type="email"
            value={emailFallback}
            onChange={(e) => setEmailFallback(e.target.value)}
            placeholder="teammate@unifirst.com"
            style={{ ...inputStyle, flex: 1 }}
          />
          <button
            type="button"
            onClick={handleAddEmail}
            disabled={busy || !emailFallback.trim()}
            style={{
              ...iconBtnStyle,
              background: 'var(--brand-600, #047857)',
              color: '#fff',
              borderColor: 'transparent',
            }}
          >
            <Mail size={13} /> Add
          </button>
        </div>
      </div>

      {/* Currently shared list */}
      <div>
        <div style={{ fontSize: 12, fontWeight: 500, color: '#3d4045', marginBottom: 6 }}>
          Currently shared with{' '}
          <span style={{ color: '#6f747b' }}>
            ({sharingState?.sharing?.length || 0})
          </span>
        </div>
        {(sharingState?.sharing || []).length === 0 ? (
          <div style={{ fontSize: 12, color: '#9da2a8' }}>No individual grants yet.</div>
        ) : (
          <ul
            style={{
              listStyle: 'none',
              margin: 0,
              padding: 0,
              border: '1px solid var(--border, #e3e5e8)',
              borderRadius: 8,
              background: '#fff',
            }}
          >
            {(sharingState?.sharing || []).map((g) => (
              <li
                key={g.userEmail}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 8,
                  padding: '8px 12px',
                  borderBottom: '1px solid var(--border-subtle, #f1f2f4)',
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>{g.userEmail}</div>
                  <div style={{ fontSize: 11, color: '#6f747b' }}>
                    {g.accessLevel} · added by {g.addedBy || 'system'}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => handleRemoveGrant(g.userEmail)}
                  disabled={busy}
                  title="Remove access for this user"
                  style={{
                    ...iconBtnStyle,
                    color: '#991b1b',
                    background: '#fff',
                    borderColor: '#fecaca',
                  }}
                >
                  <Trash2 size={13} /> Remove
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {err && (
        <div
          style={{
            padding: 8,
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: 6,
            color: '#991b1b',
            fontSize: 12,
          }}
        >
          {err}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Indexing tab — replaces the old Reindex action button. Shows the
// last-indexed timestamp / latest error and exposes the reindex
// trigger. Hidden when the agent has no knowledge source.
// ---------------------------------------------------------------------------

const IndexingTabContent: React.FC<{
  agent: MyAgent;
  reindexBusy: boolean;
  onReindex: () => void;
}> = ({ agent, reindexBusy, onReindex }) => {
  const completed =
    typeof agent.indexingCompletedAt === 'string'
      ? new Date(agent.indexingCompletedAt).toLocaleString()
      : null;
  const errorMsg =
    typeof agent.indexingError === 'string' && agent.indexingError.trim()
      ? agent.indexingError
      : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, paddingBottom: 8 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
        <div>
          <strong>Status:</strong>{' '}
          <span style={{ color: '#3d4045' }}>
            {agent.indexingStatus || 'unknown'}
          </span>
        </div>
        {completed && (
          <div style={{ fontSize: 12, color: '#6f747b' }}>
            Last indexed: {completed}
          </div>
        )}
        {errorMsg && (
          <div
            style={{
              marginTop: 6,
              padding: 8,
              background: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: 6,
              color: '#991b1b',
              fontSize: 12,
            }}
          >
            <strong>Last error:</strong> {errorMsg}
          </div>
        )}
      </div>
      <div>
        <button
          type="button"
          onClick={onReindex}
          disabled={reindexBusy}
          title={
            reindexBusy
              ? 'Reindex already running…'
              : 'Re-run SharePoint folder ingestion against the configured source'
          }
          style={{
            ...ghostBtnStyle,
            opacity: reindexBusy ? 0.5 : 1,
            cursor: reindexBusy ? 'not-allowed' : 'pointer',
          }}
        >
          <RefreshCw size={13} /> {reindexBusy ? 'Reindexing…' : 'Reindex now'}
        </button>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Edit tab — replaces the old EditAgentModal. Inline form so users
// don't lose the rest of the card context when they tweak details.
// ---------------------------------------------------------------------------

const EditTabContent: React.FC<{
  agent: MyAgent;
  onSaved: () => void;
}> = ({ agent, onSaved }) => {
  const [name, setName] = useState(agent.name);
  const [description, setDescription] = useState(
    typeof agent.description === 'string' ? agent.description : ''
  );
  const [instructions, setInstructions] = useState(
    typeof agent.instructions === 'string' ? agent.instructions : ''
  );
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);

  // Reset form when switching to a different agent.
  useEffect(() => {
    setName(agent.name);
    setDescription(typeof agent.description === 'string' ? agent.description : '');
    setInstructions(
      typeof agent.instructions === 'string' ? agent.instructions : ''
    );
    setErr(null);
    setSavedAt(null);
  }, [agent.agentId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = async () => {
    setSaving(true);
    setErr(null);
    try {
      await updateMyAgent(agent.agentId, { name, description, instructions });
      setSavedAt(new Date().toLocaleTimeString());
      onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, paddingBottom: 8 }}>
      <label style={{ display: 'block' }}>
        <div style={fieldLabelStyle}>Name</div>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          style={inputStyle}
        />
      </label>
      <label style={{ display: 'block' }}>
        <div style={fieldLabelStyle}>Description</div>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={2}
          style={inputStyle}
        />
      </label>
      <label style={{ display: 'block' }}>
        <div style={fieldLabelStyle}>System prompt / instructions</div>
        <textarea
          value={instructions}
          onChange={(e) => setInstructions(e.target.value)}
          rows={6}
          style={{ ...inputStyle, fontFamily: 'monospace', fontSize: 13 }}
        />
      </label>
      {err && (
        <div
          style={{
            padding: 8,
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: 6,
            color: '#991b1b',
            fontSize: 12,
          }}
        >
          {err}
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'flex-end' }}>
        {savedAt && !err && (
          <span style={{ fontSize: 11, color: '#10b981' }}>Saved at {savedAt}</span>
        )}
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          style={{
            ...iconBtnStyle,
            background: 'var(--brand-600, #047857)',
            color: '#fff',
            borderColor: 'transparent',
          }}
        >
          {saving ? 'Saving…' : 'Save changes'}
        </button>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Shared / available card components — read-only views for sections
// other than "Owned". They didn't change under AA-210, just kept here
// alongside the owned-card refactor for proximity.
// ---------------------------------------------------------------------------

const SharedAgentCard: React.FC<{ agent: MyAgent; onChat: () => void }> = ({ agent, onChat }) => (
  <div
    style={{
      padding: 16,
      border: '1px solid var(--border, #e3e5e8)',
      borderRadius: 10,
      background: 'var(--bg-card, #fff)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 16,
    }}
  >
    <div>
      <strong style={{ fontSize: 15 }}>{agent.name}</strong>
      <div style={{ fontSize: 12, color: '#6f747b', marginTop: 2 }}>
        Shared by {(agent.ownerEmail as string) || 'unknown'}
      </div>
    </div>
    <button type="button" onClick={onChat} style={iconBtnStyle}>
      <MessageCircle size={14} /> Chat
    </button>
  </div>
);

/**
 * Render the "via X" caption for a card in the Available-to-me
 * section. Under AA-210 the only access vector left for new agents
 * is SharePoint membership, but legacy data may still arrive with
 * other accessVia values; we keep the labels for back-compat.
 */
function _accessViaLabel(via: AccessVia | undefined, detail?: string | null): string {
  switch (via) {
    case 'sharepoint_site': {
      if (detail) {
        const m = detail.match(/\/(?:sites|teams)\/([^/?#]+)/i);
        const siteName = m ? decodeURIComponent(m[1]) : detail;
        return `Via SharePoint: ${siteName}`;
      }
      return 'Via SharePoint membership';
    }
    case 'role':
      return 'Via your role';
    case 'job_code':
      return detail ? `Via your job code (${detail})` : 'Via your job code';
    case 'location':
      return detail ? `Via your location (${detail})` : 'Via your location';
    default:
      return 'Available to you';
  }
}

const AvailableAgentCard: React.FC<{ agent: AvailableToMeAgent; onChat: () => void }> = ({
  agent,
  onChat,
}) => {
  const viaLabel = _accessViaLabel(agent.accessVia, agent.accessDetail);
  const ownerEmail = (agent.ownerEmail as string) || 'unknown';
  return (
    <div
      style={{
        padding: 16,
        border: '1px solid var(--border, #e3e5e8)',
        borderRadius: 10,
        background: 'var(--bg-card, #fff)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 16,
      }}
    >
      <div style={{ minWidth: 0 }}>
        <strong style={{ fontSize: 15 }}>{agent.name}</strong>
        {typeof agent.description === 'string' && agent.description.trim() && (
          <div
            style={{
              fontSize: 12,
              color: 'var(--text-secondary, #53575d)',
              marginTop: 2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: 480,
            }}
            title={agent.description as string}
          >
            {agent.description as string}
          </div>
        )}
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            marginTop: 6,
            fontSize: 11,
            color: 'var(--brand-700, #047857)',
            background: 'var(--brand-50, #ecfdf5)',
            border: '1px solid var(--brand-600, #047857)',
            borderRadius: 999,
            padding: '2px 8px',
          }}
        >
          {agent.accessVia === 'sharepoint_site' ? (
            <Folder size={11} aria-hidden />
          ) : (
            <Globe size={11} aria-hidden />
          )}
          {viaLabel}
        </div>
        <div style={{ fontSize: 11, color: '#9da2a8', marginTop: 4 }}>
          Owner: {ownerEmail}
        </div>
      </div>
      <button type="button" onClick={onChat} style={iconBtnStyle}>
        <MessageCircle size={14} /> Chat
      </button>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Shared button styles
// ---------------------------------------------------------------------------

const iconBtnStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '6px 10px',
  borderRadius: 6,
  border: '1px solid var(--border, #cfd2d6)',
  background: 'var(--bg-card, #fff)',
  cursor: 'pointer',
  fontSize: 12,
  color: 'var(--text-primary, #1f2124)',
};

// Filled brand button for the per-card primary action ("Chat"). Sits
// in the header so the most-likely action is the visually-loudest
// thing on the card.
const primaryBtnStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '7px 14px',
  borderRadius: 8,
  border: '1px solid var(--brand-700, #047857)',
  background: 'var(--brand-600, #047857)',
  color: '#fff',
  cursor: 'pointer',
  fontSize: 13,
  fontWeight: 500,
};

// Ghost button used inside tabs for secondary actions (Manage
// sharing, Edit details, Reindex). Subtle border + white background
// so they read as equals without competing with the primary Chat
// action above.
const ghostBtnStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '5px 10px',
  borderRadius: 6,
  border: '1px solid var(--border, #e3e5e8)',
  background: '#fff',
  cursor: 'pointer',
  fontSize: 12,
  color: 'var(--text-primary, #1f2124)',
};

// Danger ghost — used for Disable. Tinted text/border so the
// destructive intent reads at a glance, but no filled background
// so it doesn't compete with the primary action above.
const dangerGhostBtnStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '5px 10px',
  borderRadius: 6,
  border: '1px solid #fecaca',
  background: '#fff',
  cursor: 'pointer',
  fontSize: 12,
  color: '#b91c1c',
};

const fieldLabelStyle: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 500,
  marginBottom: 4,
  color: 'var(--text-primary, #1f2124)',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  borderRadius: 6,
  border: '1px solid var(--border, #cfd2d6)',
  fontSize: 14,
  boxSizing: 'border-box',
};

export default MyBuiltAgents;
